/**
 * Mobile Form Response Detail Sheet
 * 
 * Features:
 * - Bottom sheet modal for mobile
 * - Touch-friendly scrolling
 * - Grouped field sections
 * - Copy-to-clipboard support
 */

import React from 'react';
import { X, Copy, Check, ChevronDown } from 'lucide-react';
import FormResponseField from './FormResponseField';
import {
    groupResponseFieldsBySection,
    extractMetadata,
    extractContactInfo,
    formatSubmissionTime,
    formatDateDDMMMYYYY,
} from '../utils/formResponseFormatters';

/**
 * Format submitted date safely from various input types
 * @param {Date|string|object} timestamp
 * @returns {string}
 */
const formatSubmittedDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    try {
        const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
        return formatDateDDMMMYYYY(date);
    } catch {
        return 'Unknown';
    }
};

const MobileFormResponseDetailSheet = ({
    response = null,
    isOpen = false,
    onClose = () => { },
    fieldSchema = null,
}) => {
    const [copiedField, setCopiedField] = React.useState(null);
    const [expandedSections, setExpandedSections] = React.useState({});

    if (!response || !isOpen) return null;

    // Guard against missing responses object
    if (!response.responses || typeof response.responses !== 'object') {
        return (
            <>
                <div
                    className={`fixed inset-0 bg-stone-900/30 backdrop-blur-sm z-40 transition-opacity ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                    onClick={onClose}
                />
                <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-xl shadow-2xl z-50 transform transition-transform ${isOpen ? 'translate-y-0' : 'translate-y-full'}`}>
                    <div className="px-6 py-4 border-b border-notion-border bg-stone-50 flex justify-between items-center">
                        <h3 className="font-semibold text-notion-text">Form Response</h3>
                        <button onClick={onClose} className="p-1 hover:bg-stone-200 rounded">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="px-6 py-4 text-center text-stone-600">
                        <p>No form data available</p>
                    </div>
                </div>
            </>
        );
    }

    // Group fields by section
    const groupedFields = groupResponseFieldsBySection(response.responses, fieldSchema);
    const metadata = extractMetadata(response);
    const contactInfo = extractContactInfo(response);

    // Handle copy to clipboard
    const handleCopyField = (field, value) => {
        navigator.clipboard.writeText(value || '');
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section],
        }));
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-stone-900/30 backdrop-blur-sm z-40 transition-opacity ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                onClick={onClose}
            />

            {/* Sheet */}
            <div className={`fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-2xl transition-transform transform max-h-[90vh] flex flex-col ${isOpen ? 'translate-y-0' : 'translate-y-full'
                }`}>
                {/* Header */}
                <div className="px-4 py-3 border-b border-stone-200 flex items-center justify-between flex-shrink-0">
                    <div className="flex-1 min-w-0">
                        <h2 className="text-base font-bold text-notion-text truncate">{contactInfo.name}</h2>
                        <p className="text-xs text-stone-500 mt-0.5">
                            {formatSubmissionTime(response.createdAt)}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="ml-2 text-stone-400 hover:text-stone-600 transition-colors flex-shrink-0"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {/* Quick Contact Info */}
                    <div className="bg-stone-50 rounded-lg p-4 space-y-2">
                        {contactInfo.email && (
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-stone-600 truncate">{contactInfo.email}</span>
                                <button
                                    onClick={() => handleCopyField('email', contactInfo.email)}
                                    className="text-stone-400 hover:text-stone-600 transition-colors flex-shrink-0 ml-2"
                                    title="Copy email"
                                >
                                    {copiedField === 'email' ? (
                                        <Check className="w-4 h-4 text-green-600" />
                                    ) : (
                                        <Copy className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                        )}
                        {contactInfo.phone && (
                            <div className="flex justify-between items-center text-sm">
                                <a href={`tel:${contactInfo.phone}`} className="text-blue-600 hover:underline">
                                    {contactInfo.phone}
                                </a>
                                <button
                                    onClick={() => handleCopyField('phone', contactInfo.phone)}
                                    className="text-stone-400 hover:text-stone-600 transition-colors flex-shrink-0 ml-2"
                                    title="Copy phone"
                                >
                                    {copiedField === 'phone' ? (
                                        <Check className="w-4 h-4 text-green-600" />
                                    ) : (
                                        <Copy className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Response Fields - Collapsible Sections */}
                    {Object.entries(groupedFields).map(([section, fields]) => (
                        <div key={section} className="space-y-2">
                            <button
                                onClick={() => toggleSection(section)}
                                className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-stone-100 transition-colors"
                            >
                                <h3 className="text-xs font-bold text-stone-600 uppercase tracking-wider">
                                    {section}
                                </h3>
                                <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${expandedSections[section] ? 'rotate-180' : ''
                                    }`} />
                            </button>

                            {expandedSections[section] && (
                                <div className="px-3 py-3 border border-stone-200 rounded-lg space-y-3 bg-white">
                                    {fields.map((field) => (
                                        <FormResponseField
                                            key={field.id}
                                            id={field.id}
                                            label={field.label}
                                            value={field.value}
                                            type={field.type}
                                            isMetadata={false}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Metadata Section - Always Collapsible */}
                    <div className="space-y-2">
                        <button
                            onClick={() => toggleSection('metadata')}
                            className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-stone-100 transition-colors"
                        >
                            <h3 className="text-xs font-bold text-stone-600 uppercase tracking-wider">
                                Submission Info
                            </h3>
                            <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${expandedSections['metadata'] ? 'rotate-180' : ''
                                }`} />
                        </button>

                        {expandedSections['metadata'] && (
                            <div className="px-3 py-3 border border-stone-200 rounded-lg space-y-3 bg-stone-50">
                                <FormResponseField
                                    id="submittedAt"
                                    label="Submitted At"
                                    value={formatSubmittedDate(metadata.submittedAt)}
                                    type="text"
                                    isMetadata={true}
                                />
                                {metadata.pageUrl && (
                                    <FormResponseField
                                        id="pageUrl"
                                        label="Submission Page"
                                        value={metadata.pageUrl}
                                        type="url"
                                        isMetadata={true}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer - Handle Indicator */}
                <div className="px-4 py-3 flex justify-center flex-shrink-0">
                    <div className="w-12 h-1 bg-stone-300 rounded-full"></div>
                </div>
            </div>
        </>
    );
};

export default MobileFormResponseDetailSheet;
